import { useState, useEffect, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import "../waiver/waiver.css";

const API = "/api";
const STUDIO_NAME = "Iyengar Yoga St. Petersburg";

export default function MembershipAgreementPage() {
  const params         = new URLSearchParams(window.location.search);
  const emailParam     = params.get("email") ?? "";
  const nameParam      = params.get("name")  ?? "";
  const typeParam      = params.get("type")  ?? "member2x";

  const [name, setName]               = useState(nameParam);
  const [email, setEmail]             = useState(emailParam);
  const [sections, setSections]       = useState([]);
  const [details, setDetails]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState("");
  const [sigEmpty, setSigEmpty]       = useState(true);

  const sigRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/membership-agreement/text?type=${typeParam}`)
      .then(r => r.json())
      .then(data => {
        setSections(data.sections ?? []);
        setDetails(data.details ?? null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [typeParam]);

  const displaySections = sections.map(s => ({
    ...s,
    body: (s.body || "").replaceAll("[STUDENT_NAME]", name.trim() || "[STUDENT_NAME]"),
  }));

  const handleClear = () => { sigRef.current?.clear(); setSigEmpty(true); };

  const handleSubmit = async () => {
    if (!name.trim())  { setError("Please enter your full name."); return; }
    if (!email.trim() || !email.includes("@")) { setError("Please enter a valid email address."); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError("Please sign the agreement before submitting."); return;
    }
    setSubmitting(true);
    setError("");
    try {
      const signature = sigRef.current.toDataURL("image/png");
      const res = await fetch(`${API}/membership-agreement/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          membershipType: typeParam,
          signature,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? "Something went wrong."); return; }
      const back = new URLSearchParams({ subscribe: typeParam, name: name.trim(), email: email.trim().toLowerCase(), signed: "1" });
      window.location.replace(`/pricing?${back.toString()}`);
    } catch {
      setError("Network error — please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="waiver-wrap">
      <div className="waiver-inner">
        <div className="waiver-header">
          <div className="waiver-studio">{STUDIO_NAME}</div>
          <h1 className="waiver-title">Membership Agreement</h1>
          {details && (
            <p className="waiver-subtitle">
              {details.label} — ${details.price}/mo + processing fee · 3-month minimum
            </p>
          )}
          <p className="waiver-subtitle" style={{ marginTop: "0.35rem" }}>
            Please read carefully, enter your details, and sign below.
          </p>
        </div>

        <div className="waiver-fields">
          <div className="waiver-field-group">
            <label className="waiver-label">Full name</label>
            <input
              className="waiver-input"
              type="text"
              placeholder="Your full name"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>
          <div className="waiver-field-group">
            <label className="waiver-label">Email address</label>
            <input
              className="waiver-input"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div className="waiver-document">
          {loading
            ? <p className="waiver-loading">Loading agreement…</p>
            : displaySections.map((section, i) => (
                <div key={i} className="waiver-section">
                  {section.heading && <h3 className="waiver-section-heading">{section.heading}</h3>}
                  <p className="waiver-section-body">{section.body}</p>
                </div>
              ))
          }
        </div>

        <div className="waiver-sig-block">
          <div className="waiver-sig-header">
            <label className="waiver-label">Your signature</label>
            <button className="waiver-clear-btn" onClick={handleClear} type="button">Clear</button>
          </div>
          <div className="waiver-sig-canvas-wrap">
            <SignatureCanvas
              ref={sigRef}
              penColor="#1a0a12"
              canvasProps={{ className: "waiver-sig-canvas" }}
              onEnd={() => setSigEmpty(false)}
            />
            {sigEmpty && (
              <div className="waiver-sig-placeholder">Sign here with your finger or mouse</div>
            )}
          </div>
          <p className="waiver-sig-note">
            By signing above, you confirm that you have read, understood, and agree to the terms of this membership agreement.
          </p>
        </div>

        {error && <p className="waiver-error">{error}</p>}

        <button className="waiver-submit-btn" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Submitting…" : "Sign & Submit Agreement"}
        </button>

        <p className="waiver-legal">
          This electronic signature is legally binding under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act). Your signed copy will be emailed to you immediately upon submission.
        </p>
      </div>
    </div>
  );
}
